/**
 * ScrapeKing Design System v2 - Accordion Component
 * "Data Extraction Terminal" Aesthetic
 */

import React, { FC, useState, createContext, useContext } from 'react';
import styled, { css, keyframes } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const slideDown = keyframes`
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 1000px;
  }
`;

// ═══════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════

interface AccordionContextType {
  expandedItems: string[];
  toggleItem: (id: string) => void;
  allowMultiple: boolean;
}

const AccordionContext = createContext<AccordionContextType | null>(null);

// ═══════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════

const AccordionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-2);
`;

const AccordionItemWrapper = styled.div<{ $expanded: boolean }>`
  background: var(--sk-surface-default);
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-default);
  overflow: hidden;
  transition: all 0.2s ease;

  ${props => props.$expanded && css`
    border-color: var(--sk-cyan-700);
    box-shadow: 0 0 20px rgba(0, 200, 224, 0.1);
  `}
`;

const AccordionHeader = styled.button<{ $expanded: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sk-space-4);
  padding: var(--sk-space-4);
  background: ${props => props.$expanded ? 'var(--sk-bg-secondary)' : 'transparent'};
  border: none;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    background: var(--sk-surface-hover);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-3);
  flex: 1;
  min-width: 0;
`;

const HeaderIcon = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--sk-bg-tertiary);
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-sm);
  color: var(--sk-cyan-500);

  svg {
    width: 18px;
    height: 18px;
  }
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeaderTitle = styled.div`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-sm);
  font-weight: 600;
  color: var(--sk-text-primary);
`;

const HeaderSubtitle = styled.div`
  font-size: var(--sk-text-xs);
  color: var(--sk-text-tertiary);
  margin-top: var(--sk-space-1);
`;

const ChevronIcon = styled.div<{ $expanded: boolean }>`
  flex-shrink: 0;
  color: var(--sk-text-tertiary);
  transition: transform 0.2s ease;
  transform: rotate(${props => props.$expanded ? '180deg' : '0deg'});

  svg {
    width: 20px;
    height: 20px;
  }
`;

const AccordionContent = styled.div<{ $expanded: boolean }>`
  display: ${props => props.$expanded ? 'block' : 'none'};
  padding: var(--sk-space-4);
  padding-top: 0;
  animation: ${props => props.$expanded ? slideDown : 'none'} 0.3s ease;
`;

const ContentInner = styled.div`
  padding-top: var(--sk-space-4);
  border-top: 1px solid var(--sk-surface-border);
`;

// ═══════════════════════════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════════════════════════

const ChevronDownSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════════

interface AccordionProps {
  children: React.ReactNode;
  allowMultiple?: boolean;
  defaultExpanded?: string[];
  className?: string;
}

interface AccordionItemProps {
  id: string;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
}

export const Accordion: FC<AccordionProps> & {
  Item: FC<AccordionItemProps>;
} = ({
  children,
  allowMultiple = false,
  defaultExpanded = [],
  className,
}) => {
  const [expandedItems, setExpandedItems] = useState<string[]>(defaultExpanded);

  const toggleItem = (id: string) => {
    setExpandedItems(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      }
      if (allowMultiple) {
        return [...prev, id];
      }
      return [id];
    });
  };

  return (
    <AccordionContext.Provider value={{ expandedItems, toggleItem, allowMultiple }}>
      <AccordionWrapper className={className}>
        {children}
      </AccordionWrapper>
    </AccordionContext.Provider>
  );
};

const AccordionItem: FC<AccordionItemProps> = ({
  id,
  title,
  subtitle,
  icon,
  children,
  disabled,
}) => {
  const context = useContext(AccordionContext);
  if (!context) throw new Error('AccordionItem must be used within Accordion');

  const { expandedItems, toggleItem } = context;
  const isExpanded = expandedItems.includes(id);

  return (
    <AccordionItemWrapper $expanded={isExpanded}>
      <AccordionHeader
        $expanded={isExpanded}
        onClick={() => !disabled && toggleItem(id)}
        disabled={disabled}
        aria-expanded={isExpanded}
      >
        <HeaderContent>
          {icon && <HeaderIcon>{icon}</HeaderIcon>}
          <HeaderText>
            <HeaderTitle>{title}</HeaderTitle>
            {subtitle && <HeaderSubtitle>{subtitle}</HeaderSubtitle>}
          </HeaderText>
        </HeaderContent>
        <ChevronIcon $expanded={isExpanded}>
          <ChevronDownSvg />
        </ChevronIcon>
      </AccordionHeader>
      <AccordionContent $expanded={isExpanded}>
        <ContentInner>{children}</ContentInner>
      </AccordionContent>
    </AccordionItemWrapper>
  );
};

Accordion.Item = AccordionItem;

export default Accordion;
